import type { DeliveryTicketExtraction } from './api';

// Statuses on which an admin may upload a delivery ticket (applying one advances
// the load to DELIVERED). Offered from carrier-assigned through delivery.
export const TICKET_UPLOAD_STATUSES = ['ASSIGNED', 'READY_FOR_PICKUP', 'PICKED_UP', 'DELIVERED'];

// The card shows on an upload-eligible status, or on any load that already has a
// ticket (so an existing ticket stays reviewable whatever the current status).
export function shouldShowTicketCard(status: string, hasTicket: boolean): boolean {
  return TICKET_UPLOAD_STATUSES.includes(status) || hasTicket;
}

// The load fields a reviewed delivery ticket applies. Strings so they map straight
// onto the manage-load form inputs; quantity is dispatched as a number on apply.
export interface TicketApplyDraft {
  quantity: string; // delivered tons
  ticketNumber: string;
  poNumber: string;
  deliveryDate: string; // YYYY-MM-DD
}

// Mirrors server resolveDeliveredTons: Net Tons if present, else Net Pounds / 2000.
export function resolveDeliveredTons(x: DeliveryTicketExtraction): number | null {
  const tons = Number(String(x.netTons ?? '').replace(/,/g, ''));
  if (Number.isFinite(tons) && tons > 0) return Math.round(tons * 100) / 100;
  const pounds = Number(String(x.netPounds ?? '').replace(/,/g, ''));
  if (Number.isFinite(pounds) && pounds > 0) return Math.round((pounds / 2000) * 100) / 100;
  return null;
}

// Whether the ticket's load number matches the load it's filed under.
export function loadNumberMatches(
  x: DeliveryTicketExtraction,
  loadNumber: string | null | undefined
): boolean {
  const a = (x.loadNumber ?? '').trim().toUpperCase();
  const b = (loadNumber ?? '').trim().toUpperCase();
  return Boolean(a && b && a === b);
}

// The reader is asked for yyyy-mm-dd, but be defensive about m/d/yyyy scans.
export function normalizeTicketDate(value: string | null | undefined): string {
  const v = (value ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const mdy = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) {
    const [, m, d, y] = mdy;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return '';
}

export function buildTicketApplyDraft(extraction: DeliveryTicketExtraction): TicketApplyDraft {
  const tons = resolveDeliveredTons(extraction);
  return {
    quantity: tons != null ? String(tons) : '',
    ticketNumber: (extraction.ticketNumber ?? '').trim(),
    poNumber: (extraction.poNumber ?? '').trim(),
    deliveryDate: normalizeTicketDate(extraction.deliveryDate),
  };
}

export type FieldStatus = 'match' | 'change' | 'empty';

// How an extracted value compares to what's currently on the load: nothing to
// apply (empty), same as current (match), or a real change.
export function ticketFieldStatus(extracted: string, current: string): FieldStatus {
  const e = (extracted ?? '').trim();
  if (!e) return 'empty';
  return e === (current ?? '').trim() ? 'match' : 'change';
}
